"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAlphaListState = exports.hasNextPage = exports.hasPreviousPage = exports.toQueryParams = exports.shouldRefreshOnSearchReducer = exports.setSortKeyAndReverseIfSameKeyReducer = exports.setPageSizeReducer = exports.resetParamsReducer = exports.setResponseAndHeadersReducer = exports.goToPreviousPageReducer = exports.goToNextPageReducer = exports.getDefaultState = void 0;
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

const react_1 = require("react");

const types_1 = require("../types");

const useListState_1 = require("./useListState");

const initialAlphaListState = {
  contentType: '',
  currentPage: '',
  goNext: true,
  nextPage: '',
  pageSearch: '',
  pageSearchList: []
};

const getDefaultState = (options = {}) => {
  var _a, _b, _c, _d, _e, _f, _g;

  return Object.assign(Object.assign({}, useListState_1.getDefaultState(options)), {
    cacheNextTokens: (_a = options.cacheNextTokens) !== null && _a !== void 0 ? _a : false,
    contentType: (_b = options.contentType) !== null && _b !== void 0 ? _b : '',
    currentPage: (_c = options.currentPage) !== null && _c !== void 0 ? _c : '',
    goNext: (_d = options.goNext) !== null && _d !== void 0 ? _d : true,
    hasContent: null,
    nextPage: (_e = options.nextPage) !== null && _e !== void 0 ? _e : '',
    pageSearch: (_f = options.pageSearch) !== null && _f !== void 0 ? _f : '',
    pageSearchList: (_g = options.pageSearchList) !== null && _g !== void 0 ? _g : []
  });
};

exports.getDefaultState = getDefaultState;

const goToNextPageReducer = state => {
  const newState = Object.assign(Object.assign({}, state), {
    goNext: true,
    pageSearch: state.nextPage
  });

  if (state.cacheNextTokens) {
    newState.pageSearchList = [...state.pageSearchList, newState.pageSearch];
  }

  return newState;
};

exports.goToNextPageReducer = goToNextPageReducer;

const goToPreviousPageReducer = state => {
  const newState = Object.assign(Object.assign({}, state), {
    goNext: false
  });

  if (state.cacheNextTokens) {
    newState.pageSearch = newState.pageSearchList[newState.pageSearchList.length - 1];
    newState.pageSearchList = newState.pageSearchList.slice(0, newState.pageSearchList.length - 1);
  } else {
    newState.pageSearch = newState.currentPage;
  }

  return newState;
};

exports.goToPreviousPageReducer = goToPreviousPageReducer;

const setResponseAndHeadersReducer = (state, response, headers) => Object.assign(Object.assign({}, state), {
  contentType: headers.get(types_1.JIL_HEADERS.CONTENT_TYPE) || '',
  currentPage: headers.get(types_1.JIL_HEADERS.CURRENT_PAGE) || '',
  hasContent: response.length > 0 || state.filterQuery.length >= state.filterQueryMinLength,
  nextPage: headers.get(types_1.JIL_HEADERS.NEXT_PAGE) || ''
});

exports.setResponseAndHeadersReducer = setResponseAndHeadersReducer;

const resetParamsReducer = state => Object.assign(Object.assign(Object.assign({}, state), {
  pageNumber: 1
}), initialAlphaListState);

exports.resetParamsReducer = resetParamsReducer;

const setPageSizeReducer = (state, newPageSize) => {
  if (newPageSize > 0 && newPageSize !== state.pageSize) {
    return Object.assign(Object.assign(Object.assign({}, state), useListState_1.setPageSizeReducer(state, newPageSize)), initialAlphaListState);
  }

  return state;
};

exports.setPageSizeReducer = setPageSizeReducer;

const setSortKeyAndReverseIfSameKeyReducer = (state, key) => Object.assign(Object.assign(Object.assign({}, state), useListState_1.setSortKeyAndReverseIfSameKeyReducer(state, key)), initialAlphaListState);

exports.setSortKeyAndReverseIfSameKeyReducer = setSortKeyAndReverseIfSameKeyReducer;

const shouldRefreshOnSearchReducer = (state, value) => Object.assign(Object.assign(Object.assign({}, state), useListState_1.setFilterQueryReducer(state, value)), initialAlphaListState);

exports.shouldRefreshOnSearchReducer = shouldRefreshOnSearchReducer;

const toQueryParams = state => {
  return {
    pageSearch: state.pageSearch,
    pageSize: state.cacheNextTokens || state.goNext ? state.pageSize : state.pageSize * -1,
    searchQuery: state.filterQuery,
    sort: state.sortKey,
    sortOrder: state.sortReverse ? types_1.SortOrder.DESC : types_1.SortOrder.ASC
  };
};

exports.toQueryParams = toQueryParams;

const hasPreviousPage = state => {
  return state.cacheNextTokens ? state.pageSearchList.length > 0 : state.currentPage.length > 0;
};

exports.hasPreviousPage = hasPreviousPage;

const hasNextPage = state => {
  return state.nextPage.length > 0;
};

exports.hasNextPage = hasNextPage;

const alphaListStateReducer = (state, action) => {
  switch (action.type) {
    case types_1.AlphaListStateReducerActions.goToNextPage:
      return exports.goToNextPageReducer(state);

    case types_1.AlphaListStateReducerActions.goToPreviousPage:
      return exports.goToPreviousPageReducer(state);

    case types_1.AlphaListStateReducerActions.reset:
      return exports.resetParamsReducer(state);

    case types_1.AlphaListStateReducerActions.setPageSize:
      return exports.setPageSizeReducer(state, action.payload);

    case types_1.AlphaListStateReducerActions.setResponseAndHeaders:
      return exports.setResponseAndHeadersReducer(state, action.payload.response, action.payload.headers);

    case types_1.AlphaListStateReducerActions.setSortKeyAndReverseIfSameKey:
      return exports.setSortKeyAndReverseIfSameKeyReducer(state, action.payload);

    case types_1.AlphaListStateReducerActions.shouldRefreshOnSearch:
      return exports.shouldRefreshOnSearchReducer(state, action.payload);

    case types_1.ListStateReducerActions.setFilterQuery:
      return useListState_1.setFilterQueryReducer(state, action.payload);

    case types_1.ListStateReducerActions.setPageNumber:
      return useListState_1.setPageNumberReducer(state, action.payload);
  }
};
/**
 * A hook to create a @see {AlphaListState} and persist the state over a component's lifetime.
 * Exposes the computed query params for the state to pass through to APIs.
 */


const useAlphaListState = options => {
  const [state, dispatch] = react_1.useReducer(alphaListStateReducer, exports.getDefaultState(options));
  const goToNextPage = react_1.useCallback(() => {
    dispatch({
      type: types_1.AlphaListStateReducerActions.goToNextPage
    });
  }, []);
  const goToPreviousPage = react_1.useCallback(() => {
    dispatch({
      type: types_1.AlphaListStateReducerActions.goToPreviousPage
    });
  }, []);

  const _hasNextPage = react_1.useCallback(() => {
    return exports.hasNextPage(state);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- These values are required to be observed
  [state.nextPage]);

  const _hasPreviousPage = react_1.useCallback(() => {
    return exports.hasPreviousPage(state);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- These values are required to be observed
  [state.cacheNextTokens, state.pageSearchList, state.currentPage]);

  const resetParams = react_1.useCallback(() => {
    dispatch({
      type: types_1.AlphaListStateReducerActions.reset
    });
  }, []);
  const setPageSize = react_1.useCallback(newPageSize => {
    dispatch({
      payload: newPageSize,
      type: types_1.AlphaListStateReducerActions.setPageSize
    });
  }, []);
  const setResponseAndHeaders = react_1.useCallback((response, headers) => {
    dispatch({
      payload: {
        headers,
        response
      },
      type: types_1.AlphaListStateReducerActions.setResponseAndHeaders
    });
  }, []);
  const setSortKeyAndReverseIfSameKey = react_1.useCallback(key => {
    dispatch({
      payload: key,
      type: types_1.AlphaListStateReducerActions.setSortKeyAndReverseIfSameKey
    });
  }, []);
  const shouldRefreshOnSearch = react_1.useCallback(value => {
    if (value !== state.filterQuery) {
      dispatch({
        payload: value,
        type: types_1.AlphaListStateReducerActions.shouldRefreshOnSearch
      });
      return true;
    }

    return false;
  }, [state.filterQuery]);

  const _toQueryParams = react_1.useCallback(() => {
    return exports.toQueryParams(state);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- These values are required to be observed
  [state.pageSearch, state.cacheNextTokens, state.goNext, state.filterQuery, state.pageSize, state.sortKey, state.sortReverse]);
  /* List state functions */


  const setFilterQuery = react_1.useCallback(filterQuery => {
    dispatch({
      payload: filterQuery,
      type: types_1.ListStateReducerActions.setFilterQuery
    });
  }, []);
  const setPageNumber = react_1.useCallback(pageNumber => {
    dispatch({
      payload: pageNumber,
      type: types_1.ListStateReducerActions.setPageNumber
    });
  }, []);

  const _caseInsensitiveFilter = react_1.useCallback(item => {
    return useListState_1.caseInsensitiveFilter(state, item);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps -- Only the specified values are required to be observed
  [state.filterQuery, state.filterQueryKeys]);

  return {
    cacheNextTokens: state.cacheNextTokens,
    caseInsensitiveFilter: _caseInsensitiveFilter,
    contentType: state.contentType,
    currentPage: state.currentPage,
    filterQuery: state.filterQuery,
    filterQueryKeys: state.filterQueryKeys,
    filterQueryMinLength: state.filterQueryMinLength,
    goNext: state.goNext,
    goToNextPage,
    goToPreviousPage,
    hasContent: state.hasContent,
    hasNextPage: _hasNextPage,
    hasPreviousPage: _hasPreviousPage,
    nextPage: state.nextPage,
    pageNumber: state.pageNumber,
    pageSearch: state.pageSearch,
    pageSearchList: state.pageSearchList,
    pageSize: state.pageSize,
    resetParams,
    setFilterQuery,
    setPageNumber,
    setPageSize,
    setResponseAndHeaders,
    setSortKeyAndReverseIfSameKey,
    shouldRefreshOnSearch,
    sortKey: state.sortKey,
    sortReverse: state.sortReverse,
    toQueryParams: _toQueryParams
  };
};

exports.useAlphaListState = useAlphaListState;